import { Component, ViewChild, ElementRef, Renderer2, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Company, Account, CompanyExperience, CompanyUserProvider, CompanyExperienceProvider, CompanyExperienceResourceProvider, MediaProvider, Media } from '@lighty';
import { StorageService, UploadService, EmitterService, TranslateService } from '@services';
import { CommonToastService } from '@common2/services/toast.service';
import { firstValueFrom } from 'rxjs';
@Component({
    selector: 'msc-organize-experience-watch-meta-data',
    templateUrl: './meta-data.component.html',
    styleUrls: ['./meta-data.component.scss']
})

export class OrganizeExperienceWatchMetaDataComponent {
    @ViewChild('resourceUpload') resourceUpload: ElementRef;
    private company: Company;
    public resourceFile: any;
    public experience: CompanyExperience;
    public resources: any[];
    public contactUsers: any[];
    public queryContact: string;
    public loadDisplayEmitter: EventEmitter<any> = new EventEmitter();
    public loadAnnounceEmitter: EventEmitter<any> = new EventEmitter();
    public loaders: any;
    public data: any;
    public metaDataSettings: any;
    public metaData: any = {};
    public simplifiedConfig: any;

    constructor(
        private route: ActivatedRoute,
        private companyUserProvider: CompanyUserProvider,
        private companyExperienceProvider: CompanyExperienceProvider,
        private companyExperienceResourceProvider: CompanyExperienceResourceProvider,
        private storageService: StorageService,
        private toastService: CommonToastService,
        private mediaProvider: MediaProvider,
        private renderer: Renderer2,
        private uploadService: UploadService,
        private emitterService: EmitterService,
        private translateService: TranslateService,
    ) { }

    ngOnInit(): void {
        this.company = this.storageService.get('company');
        this.experience = this.storageService.get('currentExperience');
        this.resources = this.route.snapshot.data.resources;


        this.simplifiedConfig = this.initializeSimplifiedConfig(this.company.experience.metaDataSettings, this.experience.metaData);
        this.metaDataSettings = this.company.experience.metaDataSettings;


        this.data = {
            displayName: this.experience.displayName,
            announceMessage: this.experience.announceMessage,
            announceUrl: this.experience.announceUrl
        };

        this.resetLoaders();
        this.resetResource();
    }

    private resetLoaders(): void {
        this.loaders = {
            display: false,
            announce: false
        };
    }

    initializeSimplifiedConfig(metaDataSettings: any, metaData: any): any {
        const result: any = {};

        for (const sectionKey in metaDataSettings) {
            const sectionSetting = metaDataSettings[sectionKey];
            const sectionMeta = metaData?.[sectionKey];

            const sectionVisible = sectionMeta?.visible?.value ?? true;

            result[sectionKey] = {
                visible: {
                    value: sectionVisible
                },
                fields: {}
            };

            if (sectionSetting.fields) {
                for (const fieldKey in sectionSetting.fields) {
                    const fieldSetting = sectionSetting.fields[fieldKey];
                    const fieldMeta = sectionMeta?.fields?.[fieldKey];

                    const value = fieldMeta?.value ?? null;
                    const wasManuallyVisible = fieldMeta?.visible ?? false;

                    // 🔥 Auto-mostrar si hay valor (aunque sea invisible en meta)
                    const autoVisible = value !== null && value !== '';
                    const visible = wasManuallyVisible || autoVisible;

                    result[sectionKey].fields[fieldKey] = {
                        value,
                        visible
                    };
                }
            }
        }

        return result;
    }




    update(isFirtsTime?: boolean): void {
        const simplifiedConfig = this.simplifiedConfig;
        if (this.company.experience.metaDataSettings === null) {
            this.experience.metaData = this.simplifiedConfig;
        } else {
            this.experience.displayName = this.data.displayName;
            this.experience.announceMessage = this.data.announceMessage;
            this.experience.announceUrl = this.data.announceUrl;
            this.experience.metaData = simplifiedConfig;
        }

        this.companyExperienceProvider.update(this.company.slug, this.experience.id, this.experience).subscribe((experience) => {
            this.experience = this.storageService.set('currentExperience', experience);
            if (!isFirtsTime)
                this.toastService.onSuccess(this.translateService.instant('toast.saved'));
            this.resetLoaders();

            if (this.company.experience && this.company.experience.id === experience.id) {
                this.emitterService.refreshExperience.emit(true);
            }
        });
    }

    load(key: string): void {
        this.loaders[key] = true;
    }

    loadPicture(key: string): void {
        if (key === 'display') {
            this.loadDisplayEmitter.emit(true);
        } else {
            this.loadAnnounceEmitter.emit(true);
        }
    }

    updatePicture(key: string, media?: Media): void {
        this.experience[key + 'MediaId'] = media ? media.id : null;
        this.update();
    }

    focusResource(): void {
        const event = new MouseEvent('click', { bubbles: true });
        this.renderer.selectRootElement(this.resourceUpload.nativeElement).dispatchEvent(event);
    }

    loadResource(file: File): void {
        if (file) {
            this.resourceFile.file = file;
        }
    }

    uploadResource(): void {
        if (this.resourceFile.title && this.resourceFile.file) {
            this.uploadService.upload(this.resourceFile.file, 'file', 'media');
            const subscription = this.uploadService.getStatus().subscribe((data) => {
                if (data.type === 'progress') {
                    this.resourceFile.progress = data.value;
                }

                if (data.type === 'error') {
                    this.resetResource();
                    if (data.error.status === 415) {
                        this.toastService.onError(this.translateService.instant('toast.error.file-type.not-supported'));
                    }
                }

                if (data.type === 'finished') {
                    const params = {
                        media_id: data.value.id,
                        title: this.resourceFile.title
                    };
                    this.companyExperienceResourceProvider.create(this.company.slug, this.experience.id, params).subscribe((resource) => {
                        this.resources.push(resource);
                        this.toastService.onSuccess(this.translateService.instant('toast.file-uploaded'));
                        this.resetResource();
                        subscription.unsubscribe();
                    });
                }
            });
        } else {
            this.toastService.onWarning(this.translateService.instant('toast.warning.required'));
        }
    }

    private resetResource(): void {
        this.resourceFile = {
            file: '',
            title: '',
            progress: ''
        };
    }

    deleteResource(resourceId: number): void {
        this.companyExperienceResourceProvider.delete(this.company.slug, this.experience.id, resourceId).subscribe(() => {
            const index = this.resources.findIndex((resource) => {
                return resource.id === resourceId;
            });

            if (index !== -1) {
                this.resources.splice(index, 1);
            }
        });
    }

    onSelectContactUser(account: Account): void {
        this.experience.contactId = account.id;
        this.update();
    }

    getContactUserData(users) {
        return users.filter(user => user.account).map(user => ({
            id: user.id,
            label: user.account.name,
            value: user.account,
            isSelected: this.experience.contact?.id === user.id
        }));
    }

    loadMoreContactUsers(): CallableFunction {
        return (async (query, pagination) => {
            const params = {
                ...(query && { q: query }),
                page: pagination.currentPage + 1
            };

            const result = await firstValueFrom(this.companyUserProvider.getUsers(this.company.id, params));

            return {
                data: this.getContactUserData(result.users),
                pagination: result.pagination
            };
        })
    }

    public generateFirstMetaDataObject() {
        this.experience.metaDataSettings.forEach(element => {

        });
    }


    cloneWithValuesAndVisibility<T>(input: T): T {
        if (typeof input !== 'object' || input === null) {
            return input;
        }

        const result: any = Array.isArray(input) ? [] : {};

        for (const [key, val] of Object.entries(input as any)) {
            if (key === 'visible' && typeof val === 'object' && 'value' in val) {
                result.visible = (val as any).value;

            } else if (key === 'fields' && typeof val === 'object' && val !== null) {
                result.fields = {};
                for (const [fieldKey, fieldVal] of Object.entries(val as any)) {
                    result.fields[fieldKey] = {
                        value: (fieldVal as any).value,
                        visible: (fieldVal as any).visible
                    };
                }

            } else if (typeof val === 'object' && val !== null) {
                result[key] = this.cloneWithValuesAndVisibility(val);

            }
        }

        return result as T;
    }
}
